import {useState} from "react"
import {Button, useDisclosure, Spinner} from "@heroui/react"
import Badge from "@/components/general/ui/Badge"
import {AlertType, AlertActivationStatus, AlertReview} from "../alertTypes"
import {useAlertDetails} from "@/utils/hooks/useAlerts"
import {useAlertReviewActions} from "@/utils/hooks/useAlertReviewActions"
import {useUserInfo} from "@/utils/hooks/useUserLookup"
import ChannelIndicator from "@/components/archive/ChannelIndicator"
import {ThreadDrawerTrigger} from "@/features/thread-drawer"
import {AlertActionModal} from "@/components/general/ui/AlertActionModal"
import {
  IconChevronDown,
  IconChevronUp,
  IconChevronRight,
  IconAlertCircle,
  IconLock,
  IconCheck,
  IconArrowUp,
  IconUsers,
  IconMessage,
  IconMessageCircle,
  IconX,
} from "@tabler/icons-react"

// Import alert utilities from the organized utils folder
import {
  getAlertCategoryIcon,
  formatAlertCategoryName,
  formatAlertName,
  getAlertCategoryBadgeVariant,
  getAlertCategoryColors,
} from "../utils/categoryUtils"
import {
  formatActionType,
  getActionIcon,
  getActionColor,
} from "../utils/reviewUtils"
import {IconContainer} from "./IconContainer"

// Component for displaying individual comments/reviews
const CommentItem: React.FC<{review: AlertReview}> = ({review}) => {
  const {userInfo, loading} = useUserInfo(review.user_id)

  return (
    <div className="border-l-2 border-gray-200 pb-3 pl-3">
      <div className="mb-1 flex items-center gap-2">
        <div className={`${getActionColor(review.action_type)}`}>
          {getActionIcon(review.action_type, 12)}
        </div>
        <span className="text-xs font-medium text-gray-700">
          <strong>{loading ? "Loading..." : userInfo?.name || "User"}</strong>{" "}
          {formatActionType(review.action_type)}
        </span>
        <span className="text-xs text-gray-500">
          {new Date(review.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
      {review.comment && (
        <p className="ml-5 text-sm leading-relaxed text-gray-700">
          {review.comment}
        </p>
      )}
      {review.assigned_to && (
        <p className="ml-5 mt-1 text-xs text-purple-600">
          <strong>Assigned to:</strong> {review.assigned_to}
        </p>
      )}
    </div>
  )
}

interface AlertItemProps {
  alert: AlertType
  isUpdating?: boolean
  optimisticUpdateFunctions?: {
    setItemUpdating?: (alertId: string, isUpdating: boolean) => void
    updateAlertOptimistically?: (
      alertId: string,
      updates: Partial<AlertType>
    ) => void
    moveAlertBetweenLists?: (
      alertId: string,
      fromStatus: AlertActivationStatus,
      toStatus: AlertActivationStatus,
      updates?: Partial<AlertType>
    ) => void
    refetchInBackground?: () => Promise<void>
  }
}

// Component for displaying alert data
export const AlertItem: React.FC<AlertItemProps> = ({
  alert,
  isUpdating = false,
  optimisticUpdateFunctions,
}) => {
  const [open, setOpen] = useState(false)
  const [currentAction, setCurrentAction] = useState<string | null>(null)
  const [flaggedMessageExpanded, setFlaggedMessageExpanded] = useState(false)
  const [activityExpanded, setActivityExpanded] = useState(false)
  const {
    details,
    loading: detailsLoading,
    refetchDetails,
    addCommentOptimistically,
  } = useAlertDetails(open ? alert.id : null)
  const {
    resolveAlert,
    escalateAlert,
    dismissAlert,
    commentOnAlert,
    assignAlert,
    reopenAlert,
    loading: actionLoading,
    error: actionError,
  } = useAlertReviewActions({
    setItemUpdating: optimisticUpdateFunctions?.setItemUpdating,
    updateAlertOptimistically:
      optimisticUpdateFunctions?.updateAlertOptimistically,
    moveAlertBetweenLists: optimisticUpdateFunctions?.moveAlertBetweenLists,
    refetchInBackground: optimisticUpdateFunctions?.refetchInBackground,
  })

  // Modal state
  const {
    isOpen: isCommentModalOpen,
    onOpen: onCommentModalOpen,
    onOpenChange: onCommentModalOpenChange,
  } = useDisclosure()

  const {
    isOpen: isAssignModalOpen,
    onOpen: onAssignModalOpen,
    onOpenChange: onAssignModalOpenChange,
  } = useDisclosure()

  // Action handlers with individual loading states
  const handleResolve = async () => {
    setCurrentAction("resolve")
    const success = await resolveAlert(alert.id)
    if (success) {
      console.log("Alert resolved successfully")
      // Refresh details to show new review
      if (refetchDetails) {
        await refetchDetails()
      }
    }
    setCurrentAction(null)
  }

  const handleEscalate = async () => {
    setCurrentAction("escalate")
    const success = await escalateAlert(alert.id)
    if (success) {
      console.log("Alert escalated successfully")
      // Refresh details to show new review
      if (refetchDetails) {
        await refetchDetails()
      }
    }
    setCurrentAction(null)
  }

  const handleDismiss = async () => {
    setCurrentAction("dismiss")
    const success = await dismissAlert(alert.id)
    if (success) {
      console.log("Alert dismissed successfully")
      // Refresh details to show new review
      if (refetchDetails) {
        await refetchDetails()
      }
    }
    setCurrentAction(null)
  }

  const handleReopen = async () => {
    setCurrentAction("reopen")
    const success = await reopenAlert(alert.id)
    if (success) {
      console.log("Alert reopened successfully")
      // Refresh details to show new review
      if (refetchDetails) {
        await refetchDetails()
      }
    }
    setCurrentAction(null)
  }

  const handleComment = async (data: {comment?: string}): Promise<boolean> => {
    if (data.comment) {
      setCurrentAction("comment")

      // Add optimistic comment if we have the functions available
      if (addCommentOptimistically && details) {
        // We'll need to get the current user ID - for now use a placeholder
        addCommentOptimistically(data.comment, "current-user")
      }

      const success = await commentOnAlert(alert.id, data.comment)
      if (success) {
        console.log("Comment added successfully")
        // Auto-expand the Activity & Comments section to show the new comment
        setActivityExpanded(true)
        // Refresh details to get the actual comment from server
        if (refetchDetails) {
          await refetchDetails()
        }
      }
      setCurrentAction(null)
      return success
    }
    return false
  }

  const handleAssign = async (data: {
    comment?: string
    assignedTo?: string
  }): Promise<boolean> => {
    if (data.assignedTo) {
      setCurrentAction("assign")
      const success = await assignAlert(alert.id, data.assignedTo, data.comment)
      if (success) {
        console.log("Alert assigned successfully")
        // Auto-expand the Activity & Comments section to show the new assignment activity
        setActivityExpanded(true)
        // Refresh details to show new review
        if (refetchDetails) {
          await refetchDetails()
        }
      }
      setCurrentAction(null)
      return success
    }
    return false
  }

  // Format participants list similar to archive view
  const formatParticipants = (participants: string[] | undefined) => {
    if (!participants || participants.length === 0) return "No participants"

    if (participants.length === 2) {
      return participants.join(" & ")
    }
    return participants.join(", ")
  }

  return (
    <div
      className={`group relative transition-colors hover:bg-gray-50 ${isUpdating ? "opacity-70" : ""}`}
    >
      {isUpdating && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <Spinner size="sm" color="primary" />
        </div>
      )}
      <div
        className="flex cursor-pointer items-center justify-between px-4 py-3"
        onClick={() => !isUpdating && setOpen(!open)}
      >
        <div className="min-w-0 flex-1">
          {/* Subject line - exact archive styling */}
          {alert.firstMessageSubject && (
            <div className="mb-2 flex flex-row items-center gap-1">
              {/* ChannelIndicator requires special alignment due to built-in styling */}
              <ChannelIndicator
                channel_type="email"
                size="xs"
                containerClass="px-1 -ml-1"
              />
              <p className="text-sm font-medium text-blue-500">
                {alert.firstMessageSubject}
              </p>
            </div>
          )}

          {/* Alert Type - with icon to make it more obvious */}
          <div className="mb-2 flex items-center gap-2">
            <div className="flex items-center gap-1">
              <IconContainer>
                <IconAlertCircle className="h-4 w-4 text-amber-500" />
              </IconContainer>
              <span className="text-sm font-medium text-gray-700">
                {formatAlertName(alert.name)}
              </span>
            </div>
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs">
              <div className={getAlertCategoryColors(alert.category).text}>
                {getAlertCategoryIcon(alert.category, 12)}
              </div>
              <span className="font-medium text-gray-600">
                {formatAlertCategoryName(alert.category)}
              </span>
            </div>
            {alert.maxSensitivity !== null && alert.maxSensitivity > 0 && (
              <Badge variant="purple" icon={<IconLock className="h-3 w-3" />}>
                Sensitive
              </Badge>
            )}
            {alert.status === "escalated" && (
              <Badge variant="red" icon={<IconArrowUp className="h-3 w-3" />}>
                Escalated
              </Badge>
            )}
            {alert.status === "dismissed" && (
              <Badge variant="gray" icon={<IconX className="h-3 w-3" />}>
                False Positive
              </Badge>
            )}
            {alert.status === "resolved" && (
              <Badge variant="green" icon={<IconCheck className="h-3 w-3" />}>
                Resolved
              </Badge>
            )}
          </div>

          {/* Participants */}
          <p className="text-sm text-gray-600">
            {formatParticipants(alert.participants)}
          </p>
        </div>

        {/* Right side - timestamp and expand button */}
        <div className="flex items-center gap-3 text-right">
          <p className="whitespace-nowrap text-sm text-gray-500">
            {new Date(alert.timestamp).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
          <Button
            isIconOnly
            variant="light"
            size="sm"
            className="text-gray-400 hover:text-gray-600"
            onPress={() => !isUpdating && setOpen(!open)}
          >
            {open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Button>
        </div>
      </div>

      {/* Expanded Details */}
      {open && (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="space-y-4 p-4">
            {detailsLoading ? (
              <div className="flex items-center gap-2">
                <Spinner size="sm" />
                <p className="text-sm text-gray-600">Loading details...</p>
              </div>
            ) : details ? (
              <>
                {/* Reason and Category */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <h5 className="mb-1 text-sm font-medium text-gray-700">
                      Reason for Alert
                    </h5>
                    <p className="text-sm text-gray-600">
                      {details.activationMetadata?.reasoning ||
                        "Alert triggered based on detected patterns."}
                    </p>
                  </div>
                </div>

                {/* Relevant Excerpts */}
                {details.activationMetadata?.relevantExcerpts &&
                  details.activationMetadata.relevantExcerpts.length > 0 && (
                    <div>
                      <h5 className="mb-2 text-sm font-medium text-gray-700">
                        Relevant Excerpts
                      </h5>
                      <div className="space-y-2">
                        {details.activationMetadata.relevantExcerpts.map(
                          (excerpt, idx) => (
                            <div
                              key={idx}
                              className="border-l-3 border-orange-400 bg-orange-50 py-1 pl-3"
                            >
                              <p className="text-sm italic text-orange-700">
                                "{excerpt}"
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  {alert.status === "resolved" ||
                  alert.status === "dismissed" ? (
                    // Only show Re-open button for resolved/dismissed alerts
                    <Button
                      color="warning"
                      variant="flat"
                      size="sm"
                      startContent={<IconArrowUp size={14} />}
                      onPress={handleReopen}
                      isLoading={currentAction === "reopen"}
                      isDisabled={actionLoading || isUpdating}
                    >
                      Re-open
                    </Button>
                  ) : (
                    // Actions for active/unresolved alerts
                    <>
                      <Button
                        color="danger"
                        variant="flat"
                        size="sm"
                        startContent={<IconArrowUp size={14} />}
                        onPress={handleEscalate}
                        isLoading={currentAction === "escalate"}
                        isDisabled={actionLoading || isUpdating}
                      >
                        Escalate Case
                      </Button>
                      <Button
                        color="primary"
                        variant="flat"
                        size="sm"
                        startContent={<IconMessageCircle size={14} />}
                        onPress={onCommentModalOpen}
                        isDisabled={actionLoading || isUpdating}
                      >
                        Add Comment
                      </Button>
                      <Button
                        color="warning"
                        variant="flat"
                        size="sm"
                        startContent={<IconX size={14} />}
                        onPress={handleDismiss}
                        isLoading={currentAction === "dismiss"}
                        isDisabled={actionLoading || isUpdating}
                      >
                        Mark False Positive
                      </Button>
                      <Button
                        color="success"
                        variant="flat"
                        size="sm"
                        startContent={<IconCheck size={14} />}
                        onPress={handleResolve}
                        isLoading={currentAction === "resolve"}
                        isDisabled={actionLoading || isUpdating}
                      >
                        Mark as Resolved
                      </Button>
                    </>
                  )}
                </div>

                {/* Error Display */}
                {actionError && (
                  <div className="mt-2 rounded-md bg-red-50 p-2">
                    <p className="text-sm text-red-800">{actionError}</p>
                  </div>
                )}

                {/* Collapsible Flagged Message Section */}
                {details?.relatedMessage && (
                  <div className="border-t border-gray-200 pt-4">
                    <div
                      className="flex cursor-pointer items-center gap-1 rounded-md bg-gray-50 px-3 py-2 hover:bg-gray-100"
                      onClick={() =>
                        setFlaggedMessageExpanded(!flaggedMessageExpanded)
                      }
                    >
                      {flaggedMessageExpanded ? (
                        <IconChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <IconChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                      <IconContainer>
                        <IconMessage className="h-4 w-4 text-blue-500" />
                      </IconContainer>
                      <h5 className="text-sm font-medium text-gray-700">
                        Flagged Message
                      </h5>
                    </div>

                    {flaggedMessageExpanded && (
                      <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
                        {details.relatedThread?.title && (
                          <p className="mb-1 text-xs text-gray-500">
                            <strong>Thread:</strong>{" "}
                            {details.relatedThread.title}
                          </p>
                        )}
                        {details.relatedMessage?.subject && (
                          <p className="mb-2 text-xs text-gray-500">
                            <strong>Subject:</strong>{" "}
                            {details.relatedMessage.subject}
                          </p>
                        )}
                        <p className="text-sm leading-relaxed text-gray-700">
                          {details.relatedMessage.message ||
                            details.relatedMessage.raw_content ||
                            "Message content not available"}
                        </p>
                        <div className="mt-3 border-t border-gray-100 pt-2">
                          <ThreadDrawerTrigger
                            threadId={alert.threadUid}
                            messageId={alert.messageId}
                          >
                            <Button
                              size="sm"
                              color="primary"
                              variant="bordered"
                            >
                              Show Message in Conversation
                            </Button>
                          </ThreadDrawerTrigger>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Collapsible Activity & Comments Section */}
                {details?.reviews && details.reviews.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <div
                      className="flex cursor-pointer items-center gap-1 rounded-md bg-gray-50 px-3 py-2 hover:bg-gray-100"
                      onClick={() => setActivityExpanded(!activityExpanded)}
                    >
                      {activityExpanded ? (
                        <IconChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <IconChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                      <IconContainer>
                        <IconMessageCircle className="h-4 w-4 text-green-500" />
                      </IconContainer>
                      <h5 className="text-sm font-medium text-gray-700">
                        Activity & Comments
                      </h5>
                      <Badge variant="gray">{details.reviews.length}</Badge>
                    </div>

                    {activityExpanded && (
                      <div className="mt-2 space-y-3">
                        {details.reviews
                          .sort(
                            (a, b) =>
                              new Date(a.created_at).getTime() -
                              new Date(b.created_at).getTime()
                          )
                          .map((review) => (
                            <CommentItem
                              key={review.review_id}
                              review={review}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-red-600">
                Could not load alert details
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Modals */}
      <AlertActionModal
        isOpen={isCommentModalOpen}
        onOpenChange={onCommentModalOpenChange}
        title="Add Comment"
        action="comment"
        loading={currentAction === "comment"}
        onSubmit={handleComment}
      />

      <AlertActionModal
        isOpen={isAssignModalOpen}
        onOpenChange={onAssignModalOpenChange}
        title="Assign Alert"
        action="assign"
        loading={currentAction === "assign"}
        onSubmit={handleAssign}
      />
    </div>
  )
}
